import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const DataContext = createContext();

export const useData = () => useContext(DataContext);

// Project group filtering (Reliability/Security/IT/Service, keyed by raw
// Jira project codes) is retired. The owner's instruction: use Team
// everywhere instead — teams are server-defined people-rosters served from
// server/src/config/squads.json via GET /api/analytics/squads (see
// hooks/useTeamOptions), not a hardcoded project-code list.

const DEFAULT_FILTERS = {
  assignee: '',
  parentEpic: '',
  category: '',
  squad: '',
  timeFrame: 'month',
  startDate: null,
  endDate: null,
};

/**
 * Maps UI time frames onto the values the analytics API expects.
 *
 * These MUST match the vocabulary in server/src/utils/buildTicketFilter.js.
 * They previously did not: the UI sent `lastMonth`/`lastWeek`/`lastYear`, the
 * server only understood `month`/`week`/`year`, and an unrecognised value fell
 * through to an unbounded query — so every dashboard rendered all-time data
 * under a date-range label. The server now rejects unknown values outright.
 *
 * Note `all` deliberately has no date bound; it must not fall back to a default.
 */
const API_TIME_FRAME = {
  week: 'week',
  month: 'month',
  quarter: 'quarter',
  year: 'year',
  all: 'all',
};

// Helper function to build API parameters
const buildApiParams = (filters) => {
  const params = new URLSearchParams();

  if (filters.assignee) params.append('assignee', filters.assignee);
  if (filters.parentEpic) params.append('parentEpic', filters.parentEpic);
  if (filters.category) params.append('category', filters.category);
  if (filters.squad) params.append('squad', filters.squad);

  // Handle time frame or custom dates
  if (filters.timeFrame === 'custom' && filters.startDate && filters.endDate) {
    params.append('startDate', filters.startDate.toISOString().split('T')[0]);
    params.append('endDate', filters.endDate.toISOString().split('T')[0]);
  } else if (filters.timeFrame !== 'custom') {
    // Fall back to the UI default rather than silently mislabelling the window.
    params.append('timeFrame', API_TIME_FRAME[filters.timeFrame] || 'month');
  }

  return params;
};

export const DataProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [ticketTotal, setTicketTotal] = useState(0);

  const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTERS);

  // Reference to filter options so callbacks stay stable across renders.
  const filterOptionsRef = useRef(filterOptions);
  useEffect(() => {
    filterOptionsRef.current = filterOptions;
  }, [filterOptions]);

  // State for analytics data
  const [summaryData, setSummaryData] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [assigneeData, setAssigneeData] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [epicData, setEpicData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initialLoadDone = useRef(false);

  /**
   * Fetches a single page of tickets. The dataset can run to tens of thousands
   * of rows, so we deliberately page rather than pulling everything into memory.
   */
  const fetchTickets = useCallback(async (filters = {}, { page = 1, limit = 250 } = {}) => {
    try {
      setLoading(true);
      setError(null);

      const combinedFilters = { ...filterOptionsRef.current, ...filters };
      const params = buildApiParams(combinedFilters);
      params.set('page', String(page));
      params.set('limit', String(limit));

      const response = await api.get(`/tickets?${params.toString()}`);
      const ticketsData = response.data.tickets || [];
      const pagination = response.data.pagination || {};

      setTickets(ticketsData);
      setFilteredTickets(ticketsData);
      setTicketTotal(pagination.total || ticketsData.length);
      setFilterOptions(combinedFilters);

      return { tickets: ticketsData, pagination };
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch tickets');
      setTickets([]);
      setFilteredTickets([]);
      setTicketTotal(0);
      return { tickets: [], pagination: {} };
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch analytics data based on current filters
  const fetchAnalytics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const combinedFilters = { ...filterOptionsRef.current, ...filters };
      const qs = buildApiParams(combinedFilters).toString();

      const [summary, categories, assignees, timeSeries, epics] = await Promise.all([
        api.get(`/analytics/summary?${qs}`),
        api.get(`/analytics/categories?${qs}`),
        api.get(`/analytics/assignees?${qs}`),
        api.get(`/analytics/timeseries?${qs}`),
        api.get(`/analytics/epics?${qs}`),
      ]);

      setSummaryData(summary.data);
      setCategoryData(categories.data);
      setAssigneeData(assignees.data);
      setTimeSeriesData(timeSeries.data);
      setEpicData(epics.data);
      setFilterOptions(combinedFilters);

      return {
        summary: summary.data,
        categories: categories.data,
        assignees: assignees.data,
        timeSeries: timeSeries.data,
        epics: epics.data,
      };
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load analytics data');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload JIRA export file
  const uploadJiraExport = useCallback(
    async (file) => {
      try {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        await fetchTickets();
        await fetchAnalytics();

        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to upload JIRA export');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fetchTickets, fetchAnalytics]
  );

  /** Client-side search across the currently loaded page of tickets. */
  const applyLocalFilter = useCallback(
    (filter) => {
      if (!filter?.search) {
        setFilteredTickets(tickets);
        return;
      }
      const needle = filter.search.toLowerCase();
      setFilteredTickets(
        tickets.filter(
          (t) =>
            t.key?.toLowerCase().includes(needle) ||
            t.summary?.toLowerCase().includes(needle) ||
            t.description?.toLowerCase().includes(needle)
        )
      );
    },
    [tickets]
  );

  const clearFilters = useCallback(() => {
    setFilterOptions(DEFAULT_FILTERS);
    fetchTickets(DEFAULT_FILTERS);
    fetchAnalytics(DEFAULT_FILTERS);
  }, [fetchTickets, fetchAnalytics]);

  // Load initial data exactly once on mount.
  useEffect(() => {
    if (initialLoadDone.current || authLoading || !isAuthenticated) return undefined;
    let isMounted = true;

    (async () => {
      try {
        await fetchTickets();
        if (!isMounted) return;
        await fetchAnalytics();
        initialLoadDone.current = true;
      } catch {
        if (isMounted) setError('Failed to load initial data');
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [fetchTickets, fetchAnalytics, authLoading, isAuthenticated]);

  return (
    <DataContext.Provider
      value={{
        tickets,
        filteredTickets,
        ticketTotal,
        summaryData,
        categoryData,
        assigneeData,
        timeSeriesData,
        epicData,

        filterOptions,
        setFilterOptions,

        loading,
        error,

        fetchTickets,
        fetchAnalytics,
        uploadJiraExport,
        applyLocalFilter,
        clearFilters,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
